import * as Yup from "yup";

import {
  APP_TYPE,
  PARTNER_PICKS_REPORTINGS,
  PARTNER_PICKS_TYPES,
  RECOMMENDATION_TYPES,
  TIVO_LAUNCH_TYPES,
  TiVoTargets,
  URL_PARAMETER_PLACEMENT,
} from "hooks/tivoApps";
import {
  applyJsonValidations,
  applyNumberValidations,
  applyStringValidations,
  createValidationSchema,
  FieldsConfig,
} from "utils/validation";

import { TEXTS } from "./constants";
import {
  FIELDS_LAUNCH_CONFIG_TAB,
  FIELDS_PRESENTATION_CONFIG,
  FIELDS_PRESENTATION,
  FIELDS_ZOOM_CONFIG,
  PROVIDER_FIELDS,
  VARIANT_FIELDS,
} from "./fields";

const atLeastOneMessage = (fieldLabel: string, itemLabel: string) =>
  `${fieldLabel} must include at least 1 ${itemLabel}.`;

const requiredMessage = (fieldLabel: string) => `${fieldLabel} is required.`;

function buildRegExpTest(fieldLabel: string) {
  return {
    name: "is-reg-exp-valid",
    test(value: string, ctx: any) {
      if (!value) {
        return true;
      }
      try {
        RegExp(value);
      } catch {
        return ctx.createError({
          message: `${fieldLabel} is not a valid regular expression.`,
        });
      }
      return true;
    },
  };
}

function buildTargetsTest(fieldLabel: string) {
  return {
    name: "are-targets-valid",
    test(targets: TiVoTargets, ctx: any) {
      if (
        Object.keys(targets || {}).length > 0 &&
        Object.values(targets || {}).some((group) => group.length > 0)
      ) {
        return true;
      }
      return ctx.createError({
        message: `${fieldLabel} is required.`,
      });
    },
  };
}

export function createProviderValidationSchema(fields: FieldsConfig) {
  const presentationConfigSchema = Yup.object()
    .required()
    .shape({
      attributes: Yup.object()
        .required()
        .shape({
          alwaysVisibleInMyApps: Yup.string().oneOf(["on", "off", "inherited"]),
          appManifestAvailable: Yup.string().oneOf(["on", "off", "inherited"]),
          browsePlaybackSortOrder: applyNumberValidations(
            Yup.number(),
            FIELDS_PRESENTATION_CONFIG.BROWSE_PLAYBACK_SORT_ORDER
          ),
          enabledByDefault: Yup.string().oneOf(["on", "off", "inherited"]),
          inMyAppsByDefault: Yup.string().oneOf(["on", "off", "inherited"]),
          myAppsRangeGroup: applyNumberValidations(
            Yup.number(),
            FIELDS_PRESENTATION_CONFIG.MY_APPS_RANGE_GROUP
          ),
          name: applyStringValidations(
            Yup.string(),
            FIELDS_PRESENTATION_CONFIG.NAME
          ),
          platformSupportedApps: Yup.array().of(
            Yup.string().required(
              requiredMessage(
                TEXTS.PRESENTATION.PLATFORM_SUPPORTED_APP_PLACEHOLDER
              )
            )
          ),
          promoted: Yup.string().oneOf(["on", "off", "inherited"]),
          publishDateTime: Yup.string(),
          recommendationsType: Yup.string()
            .oneOf(Object.keys(RECOMMENDATION_TYPES))
            .required(
              requiredMessage(
                FIELDS_PRESENTATION_CONFIG.RECOMMENDATION_TYPE.label
              )
            ),
          postPublishDaysConsideredNew: applyNumberValidations(
            Yup.number(),
            FIELDS_PRESENTATION_CONFIG.POST_PUBLISH_DAYS_CONSIDERED_NEW
          ),
          shouldShowInList: Yup.string().oneOf(["on", "off", "inherited"]),
          shouldShowInSearchInApps: Yup.string().oneOf([
            "on",
            "off",
            "inherited",
          ]),
          sortOrder: applyNumberValidations(
            Yup.number().transform((value, originalValue) =>
              originalValue === "" ? undefined : value
            ),
            FIELDS_PRESENTATION_CONFIG.SORT_ORDER
          ),
          supportsEpisodeDeepLinks: Yup.string().oneOf([
            "on",
            "off",
            "inherited",
          ]),
        }),
      countries: Yup.array().when("matchByPlatformSupportedFeatures", {
        is: false,
        then: (schema) =>
          schema
            .min(
              1,
              atLeastOneMessage(
                FIELDS_PRESENTATION_CONFIG.COUNTRIES.label,
                "country"
              )
            )
            .of(Yup.string().required()),
      }),
      platformSupportedFeatures: Yup.array().when(
        "matchByPlatformSupportedFeatures",
        {
          is: true,
          then: (schema) =>
            schema
              .min(
                1,
                atLeastOneMessage(
                  FIELDS_PRESENTATION_CONFIG.PLATFORM_SUPPORTED_FEATURES.label,
                  "feature"
                )
              )
              .of(
                Yup.string().required(
                  requiredMessage(
                    TEXTS.PRESENTATION.PLATFORM_SUPPORTED_FEATURE_PLACEHOLDER
                  )
                )
              ),
        }
      ),
    })
    .test("default-config-sort-order-required", (config, ctx) => {
      const isDefaultConfig =
        !config?.countries && !config?.platformSupportedFeatures;
      const sortOrder = config?.attributes?.sortOrder;

      if (!isDefaultConfig || (sortOrder !== undefined && sortOrder !== null)) {
        return true;
      }
      return ctx.createError({
        path: `${ctx.path}.attributes.sortOrder`,
        message: requiredMessage(FIELDS_PRESENTATION_CONFIG.SORT_ORDER.label),
      });
    });

  const presentationsSchema = Yup.array()
    .required()
    .of(
      Yup.object()
        .required()
        .shape({
          releaseStage: Yup.string().oneOf(["rs1", "rs2", "rs3"]).required(),
          configs: Yup.array().required().of(presentationConfigSchema),
          label: applyStringValidations(
            Yup.string().required(
              requiredMessage(FIELDS_PRESENTATION.LABEL.label)
            ),
            FIELDS_PRESENTATION.LABEL
          ),
          targets: Yup.object()
            .required()
            .shape({
              platforms: Yup.array().of(Yup.string().required()),
              oems: Yup.array().of(Yup.string().required()),
              clientTypes: Yup.array().of(Yup.string().required()),
            })
            .test(buildTargetsTest(FIELDS_PRESENTATION.TARGETS.label)),
          partnerPicks: Yup.object()
            .required()
            .shape({
              aspectRatio: applyStringValidations(
                Yup.string(),
                FIELDS_PRESENTATION.PARTNER_PICKS_ASPECT_RATIO
              ),
              reporting: Yup.string().oneOf(
                Object.keys(PARTNER_PICKS_REPORTINGS)
              ),
              type: Yup.string().oneOf(Object.keys(PARTNER_PICKS_TYPES)),
            }),
        })
    );

  return createValidationSchema(fields)
    .omit(["allowList", "presentations"])
    .concat(
      Yup.object()
        .required()
        .shape({
          countries: fields.COUNTRIES
            ? Yup.array().when("worldwide", {
                is: "false",
                then: (schema) =>
                  schema
                    .required()
                    .min(
                      1,
                      atLeastOneMessage(
                        PROVIDER_FIELDS.COUNTRIES.SUPPORTED_COUNTRIES.label,
                        "country"
                      )
                    ),
              })
            : Yup.mixed(),
          allowList: fields.BASE?.ALLOW_LIST
            ? Yup.array().of(
                Yup.string()
                  .required(requiredMessage(TEXTS.BASE.REG_EXP_PLACEHOLDER))
                  .test(buildRegExpTest(TEXTS.BASE.REG_EXP_PLACEHOLDER))
              )
            : Yup.mixed(),
          presentations: presentationsSchema,
        })
    );
}

export function createVariantValidationSchema(fields: FieldsConfig) {
  const launchAttributesSchema = Yup.object()
    .required()
    .shape({
      appType: Yup.string()
        .oneOf(Object.keys(APP_TYPE))
        .required(requiredMessage(VARIANT_FIELDS.LAUNCH_CONFIG.APP_TYPE.label)),
    })
    .shape(
      TIVO_LAUNCH_TYPES.reduce<Yup.ObjectShape>(
        (acc, cur) => ({
          ...acc,
          [cur]: Yup.lazy((value) => {
            if (
              value === undefined ||
              value === null ||
              (typeof value === "object" && Object.keys(value).length === 0)
            ) {
              return Yup.mixed().notRequired();
            }

            return Yup.object()
              .required()
              .shape({
                adParamsTemplate: applyStringValidations(
                  Yup.string(),
                  FIELDS_LAUNCH_CONFIG_TAB.AD_PARAMS
                ),
                appendUrlParameters: Yup.array().of(
                  Yup.object()
                    .required()
                    .shape({
                      key: Yup.string().required(
                        requiredMessage(TEXTS.LAUNCH_CONFIG.KEY_PLACEHOLDER)
                      ),
                      name: Yup.string().required(
                        requiredMessage(TEXTS.LAUNCH_CONFIG.NAME_PLACEHOLDER)
                      ),
                      placement: Yup.string().oneOf(
                        Object.keys(URL_PARAMETER_PLACEMENT)
                      ),
                      value: Yup.string(),
                    })
                ),
                attributesWithCriteria: applyJsonValidations(
                  applyStringValidations(
                    Yup.string(),
                    FIELDS_LAUNCH_CONFIG_TAB.ATTRS_WITH_CRIT
                  ),
                  FIELDS_LAUNCH_CONFIG_TAB.ATTRS_WITH_CRIT
                ),
                intent: Yup.string().when("validationMode", {
                  is: "intent",
                  then: (schema) =>
                    applyJsonValidations(
                      applyStringValidations(
                        schema.required(
                          requiredMessage(FIELDS_LAUNCH_CONFIG_TAB.INTENT.label)
                        ),
                        FIELDS_LAUNCH_CONFIG_TAB.INTENT
                      ),
                      FIELDS_LAUNCH_CONFIG_TAB.INTENT
                    ),
                }),
                playbackUri: Yup.string().when("validationMode", {
                  is: "uri",
                  then: (schema) =>
                    applyStringValidations(
                      schema.required(
                        requiredMessage(
                          FIELDS_LAUNCH_CONFIG_TAB.PLAYBACK_URI.label
                        )
                      ),
                      FIELDS_LAUNCH_CONFIG_TAB.PLAYBACK_URI
                    ),
                }),
              });
          }),
        }),
        {}
      )
    )
    .test(
      "at-least-one-launch-type",
      atLeastOneMessage(TEXTS.LAUNCH_CONFIG.ATTRIBUTES_HEADER, "launch type"),
      (value) =>
        TIVO_LAUNCH_TYPES.some(
          (launchType) => Object.keys(value?.[launchType] || {}).length > 0
        )
    );
  const uriTransformSchema = Yup.object()
    .required()
    .shape({
      alternateBasePlaybackUrl: Yup.string(),
      className: Yup.string(),
      enabled: Yup.boolean().required(),
      providerSpecificNameValueMap: Yup.array().of(
        Yup.object().shape({
          name: Yup.string().required(
            requiredMessage(TEXTS.URI_TRANSFORM.NAME_PLACEHOLDER)
          ),
          value: Yup.string().required(
            requiredMessage(TEXTS.URI_TRANSFORM.VALUE_PLACEHOLDER)
          ),
        })
      ),
    });

  const zoomConfigurationsSchema = Yup.array()
    .required()
    .of(
      Yup.object()
        .required()
        .shape({
          targets: Yup.object()
            .required()
            .shape({
              platforms: Yup.array().of(Yup.string().required()),
              oems: Yup.array().of(Yup.string().required()),
              clientTypes: Yup.array().of(Yup.string().required()),
            })
            .test(buildTargetsTest(FIELDS_ZOOM_CONFIG.TARGETS.label)),
          zoomFactor: applyNumberValidations(
            Yup.number().integer("Zoom Factor must be an integer"),
            FIELDS_ZOOM_CONFIG.ZOOM_FACTOR
          ),
          zoomFactorRatio: applyNumberValidations(
            Yup.number(),
            FIELDS_ZOOM_CONFIG.ZOOM_FACTOR_RATIO
          ),
        })
    );
  return (
    createValidationSchema(fields)
      // It is not possible to omit nested fields: https://github.com/jquense/yup/issues/1888.
      .omit([
        "countries",
        "launchAttributes",
        "targets",
        "uriTransform",
        "zoomConfigurations",
      ])
      .concat(
        Yup.object()
          .required()
          .shape({
            adsEnabled: Yup.string()
              .oneOf(["true", "false"])
              .required(
                requiredMessage(VARIANT_FIELDS.LAUNCH_CONFIG.ADS_ENABLED.label)
              ),
            countries: fields.COUNTRIES
              ? Yup.array().when("worldwide", {
                  is: "false",
                  then: (schema) =>
                    schema
                      .required()
                      .min(
                        1,
                        atLeastOneMessage(
                          PROVIDER_FIELDS.COUNTRIES.SUPPORTED_COUNTRIES.label,
                          "country"
                        )
                      ),
                })
              : Yup.mixed(),
            targets: Yup.object()
              .required()
              .shape({
                platforms: Yup.array().of(Yup.string().required()),
                oems: Yup.array().of(Yup.string().required()),
                clientTypes: Yup.array().of(Yup.string().required()),
              })
              .test(
                buildTargetsTest(VARIANT_FIELDS.LAUNCH_CONFIG.TARGETS.label)
              ),
            launchAttributes: launchAttributesSchema,
            launchAttributesTabs: Yup.array().of(
              Yup.object().shape({
                launchType: Yup.string()
                  .oneOf(TIVO_LAUNCH_TYPES)
                  .required(
                    requiredMessage(TEXTS.LAUNCH_CONFIG.TAB_LAUNCH_TYPE_LABEL)
                  ),
                launchMode: Yup.string()
                  .oneOf(["intent", "uri"])
                  .required(
                    requiredMessage(TEXTS.LAUNCH_CONFIG.TAB_LAUNCH_MODE_LABEL)
                  ),
              })
            ),
            uriTransform: uriTransformSchema,
            zoomConfigurations: zoomConfigurationsSchema,
          })
      )
  );
}
